import React, { useState, useEffect } from "react";
import {
  Template,
  templateService,
  CreateTemplateSection,
  UpdateTemplateRequest,
} from "../services/templateService";
import {
  Plus,
  FileText,
  Search,
  Loader2,
  Trash2,
  X,
  Layout,
  Settings,
} from "lucide-react";
import { format } from "date-fns";

const emptyTemplate = {
  name: "",
  description: "",
  isDefault: false,
  sections: [
    {
      title: "",
      instructions: "",
    },
  ],
};

interface ValidationErrors {
  name?: string;
  sections?: { title?: string; instructions?: string }[];
}

type ActiveSection = "template-builder" | "learn-format" | "custom-format";

const AIStudio: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState(emptyTemplate);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [activeSection, setActiveSection] =
    useState<ActiveSection>("template-builder");
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<UpdateTemplateRequest | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplateDetails = async (id: number) => {
    try {
      setIsLoadingTemplate(true);
      setError(null);
      const templateData = await templateService.getTemplate(id);
      if (!templateData || !templateData.sections) {
        throw new Error("Invalid template data received");
      }
      setSelectedTemplate(templateData);
    } catch (err) {
      console.error("Error fetching template details:", err);
      setError("Failed to load template details");
      setSelectedTemplate(null);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleTemplateSelect = (template: Template) => {
    if (template && template.id) {
      fetchTemplateDetails(template.id);
    }
  };

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await templateService.getTemplates();
      setTemplates(data);
      if (data.length > 0 && !selectedTemplate) {
        fetchTemplateDetails(data[0].id);
      }
    } catch (err) {
      setError("Failed to load templates");
      console.error("Error fetching templates:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setIsDeleting(true);
      await templateService.deleteTemplate(selectedTemplate.id);

      // Update templates first
      const updatedTemplates = templates.filter(
        (t) => t.id !== selectedTemplate.id
      );
      setTemplates(updatedTemplates);

      // Then update selected template
      const nextTemplate =
        updatedTemplates.length > 0 ? updatedTemplates[0] : null;
      
      if (nextTemplate) {
        fetchTemplateDetails(nextTemplate.id);
      } else {
        setSelectedTemplate(null);
      }

      setShowDeleteConfirm(false);
    } catch (err) {
      console.error("Error deleting template:", err);
      setError("Failed to delete template");
    } finally {
      setIsDeleting(false);
    }
  };

  const validateTemplate = () => {
    const errors: ValidationErrors = {};

    // Validate name
    if (!newTemplate.name.trim()) {
      errors.name = "Template name is required";
    }

    // Validate sections
    const sectionErrors = newTemplate.sections.map((section) => {
      const error: { title?: string; instructions?: string } = {};
      if (!section.title.trim()) {
        error.title = "Section title is required";
      }
      if (!section.instructions.trim()) {
        error.instructions = "Section instructions are required";
      }
      return error;
    });

    if (sectionErrors.some((error) => Object.keys(error).length > 0)) {
      errors.sections = sectionErrors;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateTemplate = async () => {
    if (!validateTemplate()) {
      return;
    }

    try {
      setIsCreating(true);
      const created = await templateService.createTemplate(newTemplate);
      await fetchTemplates();
      
      // Make sure we load the complete template details
      if (created && created.id) {
        fetchTemplateDetails(created.id);
      }
      
      setShowCreateModal(false);
      setNewTemplate(emptyTemplate);
      setValidationErrors({});
    } catch (err) {
      console.error("Error creating template:", err);
      setError("Failed to create template");
    } finally {
      setIsCreating(false);
    }
  };

  const addSection = () => {
    setNewTemplate({
      ...newTemplate,
      sections: [...newTemplate.sections, { title: "", instructions: "" }],
    });
  };

  const updateSection = (
    index: number,
    field: keyof CreateTemplateSection,
    value: string
  ) => {
    const updatedSections = [...newTemplate.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value,
    };
    setNewTemplate({
      ...newTemplate,
      sections: updatedSections,
    });
  };

  const removeSection = (index: number) => {
    setNewTemplate({
      ...newTemplate,
      sections: newTemplate.sections.filter((_, i) => i !== index),
    });
    // Also remove validation errors for this section
    if (validationErrors.sections) {
      setValidationErrors({
        ...validationErrors,
        sections: validationErrors.sections.filter((_, i) => i !== index),
      });
    }
  };

  const handleEditClick = () => {
    if (!selectedTemplate) return;

    setEditingTemplate({
      name: selectedTemplate.name,
      description: selectedTemplate.description || "",
      is_default: selectedTemplate.is_default,
      sections: selectedTemplate.sections.map((section) => ({
        id: section.id,
        title: section.title,
        instructions: section.instructions,
        order: section.order,
      })),
    });
    setIsEditing(true);
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate || !editingTemplate) return;

    try {
      setIsCreating(true);
      const updated = await templateService.updateTemplate(
        selectedTemplate.id,
        editingTemplate
      );
      await fetchTemplates();
      setSelectedTemplate(updated);
      setIsEditing(false);
      setEditingTemplate(null);
    } catch (err) {
      console.error("Error updating template:", err);
      setError("Failed to update template");
    } finally {
      setIsCreating(false);
    }
  };

  const updateEditingSection = (
    index: number,
    field: keyof (typeof editingTemplate.sections)[0],
    value: string
  ) => {
    if (!editingTemplate) return;

    const updatedSections = [...editingTemplate.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value,
    };
    setEditingTemplate({
      ...editingTemplate,
      sections: updatedSections,
    });
  };

  const addEditingSection = () => {
    if (!editingTemplate) return;

    setEditingTemplate({
      ...editingTemplate,
      sections: [
        ...editingTemplate.sections,
        {
          title: "",
          instructions: "",
          order: editingTemplate.sections.length + 1,
        },
      ],
    });
  };

  const removeEditingSection = (index: number) => {
    if (!editingTemplate) return;

    setEditingTemplate({
      ...editingTemplate,
      sections: editingTemplate.sections.filter((_, i) => i !== index),
    });
  };

  const filteredTemplates =
    templates?.filter(
      (template) =>
        template &&
        (template.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.description
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          "")
    ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={fetchTemplates}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Left Sidebar - Navigation */}
      <div className="w-80 border-r border-gray-200 bg-white overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-semibold text-gray-900">AI Studio</h2>
          </div>
          <p className="text-sm text-gray-500">
            Set custom rules for your Scribes
          </p>
        </div>

        {/* Template Builder Section */}
        <button
          onClick={() => setActiveSection("template-builder")}
          className={`p-4 border-b border-gray-200 text-left transition-colors ${
            activeSection === "template-builder"
              ? "bg-blue-50 border-l-4 border-l-blue-500"
              : "hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Layout
              className={`w-4 h-4 ${
                activeSection === "template-builder"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            />
            <h2
              className={`font-medium ${
                activeSection === "template-builder"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            >
              Template Builder
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            Create custom templates with sections that fit your needs. Scribe
            will follow the defined note structure.
          </p>
        </button>

        {/* Learn Format Section */}
        <button
          onClick={() => setActiveSection("learn-format")}
          className={`p-4 border-b border-gray-200 text-left transition-colors ${
            activeSection === "learn-format"
              ? "bg-blue-50 border-l-4 border-l-blue-500"
              : "hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText
              className={`w-4 h-4 ${
                activeSection === "learn-format"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            />
            <h2
              className={`font-medium ${
                activeSection === "learn-format"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            >
              Learn Format
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            Save examples for each section of your template. Scribe will learn
            and adapt to their style and structure.
          </p>
        </button>

        {/* Custom Formatting Section */}
        <button
          onClick={() => setActiveSection("custom-format")}
          className={`p-4 border-b border-gray-200 text-left transition-colors ${
            activeSection === "custom-format"
              ? "bg-blue-50 border-l-4 border-l-blue-500"
              : "hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Settings
              className={`w-4 h-4 ${
                activeSection === "custom-format"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            />
            <h2
              className={`font-medium ${
                activeSection === "custom-format"
                  ? "text-blue-600"
                  : "text-gray-600"
              }`}
            >
              Custom Formatting
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            Set formatting rules for each of your templates. Scribe will
            generate new notes based on your requirements.
          </p>
        </button>
      </div>

      {/* Middle Section - Content based on active section */}
      {activeSection === "template-builder" && (
        <div className="w-80 border-r border-gray-200 bg-white overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {Array.isArray(templates) && templates.length > 0 ? (
              filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className={`flex items-center w-full px-4 py-3 cursor-pointer transition-colors border-b border-gray-100 ${
                    selectedTemplate?.id === template.id
                      ? "bg-blue-50 text-blue-600 border-l-4 border-l-blue-500 pl-3"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <FileText
                    className={`w-5 h-5 mr-3 flex-shrink-0 ${
                      selectedTemplate?.id === template.id
                        ? "text-blue-600"
                        : "text-gray-400"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium flex-1 ${
                      selectedTemplate?.id === template.id
                        ? "text-blue-600"
                        : "text-gray-900"
                    }`}
                  >
                    {template.name}
                  </span>
                  {template.is_default && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 font-medium rounded-full">
                      Default
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                No templates available
              </div>
            )}
          </div>
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Plus className="w-5 h-5" />
              New Template
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Template Details or Section Content */}
      <div className="flex-1 overflow-y-auto">
        {activeSection === "template-builder" && selectedTemplate ? (
          <div className="p-8">
            {isLoadingTemplate ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        {isEditing && editingTemplate ? (
                          <div className="space-y-4">
                            <input
                              type="text"
                              value={editingTemplate.name}
                              onChange={(e) =>
                                setEditingTemplate((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        name: e.target.value,
                                      }
                                    : null
                                )
                              }
                              className="w-full px-4 py-2 text-2xl font-semibold border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter template name"
                            />
                            <textarea
                              value={editingTemplate.description}
                              onChange={(e) =>
                                setEditingTemplate((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        description: e.target.value,
                                      }
                                    : null
                                )
                              }
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows={3}
                              placeholder="Enter template description"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-3">
                              <h1 className="text-2xl font-semibold text-gray-900">
                                {selectedTemplate.name}
                              </h1>
                              {selectedTemplate.is_default && (
                                <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                                  Default Template
                                </span>
                              )}
                            </div>
                            <p className="mt-2 text-gray-600">
                              {selectedTemplate.description}
                            </p>
                          </>
                        )}
                      </div>
                      <div>
                        {!isEditing && selectedTemplate && !selectedTemplate.is_default && (
                          <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                      Sections
                    </h2>
                    <div className="space-y-6">
                      {isEditing
                        ? editingTemplate?.sections.map((section, index) => (
                            <div
                              key={section.id || index}
                              className="p-4 border rounded-lg bg-gray-50"
                            >
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <input
                                    type="text"
                                    value={section.title}
                                    onChange={(e) =>
                                      updateEditingSection(
                                        index,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter section title"
                                  />
                                  {editingTemplate.sections.length > 1 && (
                                    <button
                                      onClick={() =>
                                        removeEditingSection(index)
                                      }
                                      className="ml-2 p-1 text-gray-400 hover:text-red-500"
                                    >
                                      <X className="w-5 h-5" />
                                    </button>
                                  )}
                                </div>
                                <textarea
                                  value={section.instructions}
                                  onChange={(e) =>
                                    updateEditingSection(
                                      index,
                                      "instructions",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows={3}
                                  placeholder="Enter section instructions"
                                />
                              </div>
                            </div>
                          ))
                        : selectedTemplate && selectedTemplate.sections && selectedTemplate.sections.map((section) => (
                            <div
                              key={section.id}
                              className="bg-gray-50 rounded-lg p-4"
                            >
                              <h3 className="text-md font-medium text-gray-900 mb-2">
                                {section.title}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {section.instructions}
                              </p>
                            </div>
                          ))}
                      {isEditing && (
                        <button
                          onClick={addEditingSection}
                          className="w-full py-3 border-2 border-dashed border-blue-200 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Add Section
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="p-6 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-end gap-3">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setEditingTemplate(null);
                            }}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdateTemplate}
                            disabled={isCreating}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isCreating && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                            Save Changes
                          </button>
                        </>
                      ) : (
                        <>
                          {selectedTemplate && !selectedTemplate.is_default && (
                            <button
                              onClick={handleEditClick}
                              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                              Edit Template
                            </button>
                          )}
                          
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeSection === "learn-format" ? (
          <div className="p-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-semibold mb-4">Learn Format</h2>
              <p className="text-gray-600 mb-8">
                Train your AI to understand your preferred note-taking style
              </p>
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-24 h-24 mb-8 relative">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Coming Soon</h3>
                <p className="text-gray-600 max-w-md">
                  We're working hard to bring you the Learn Format feature. This will allow you to train your AI to understand your specific note-taking style and preferences.
                </p>
              </div>
            </div>
          </div>
        ) : activeSection === "custom-format" ? (
          <div className="p-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-semibold mb-4">Custom Formatting</h2>
              <p className="text-gray-600 mb-8">
                Customize how your notes are formatted
              </p>
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-24 h-24 mb-8 relative">
                  <div className="absolute inset-0 border-4 border-purple-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Coming Soon</h3>
                <p className="text-gray-600 max-w-md">
                  We're currently developing the Custom Formatting feature. Soon you'll be able to set specific formatting rules for your templates and customize the appearance of your generated notes.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select a template to view details
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Create New Template
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTemplate(emptyTemplate);
                    setValidationErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) =>
                      setNewTemplate({ ...newTemplate, name: e.target.value })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.name ? "border-red-500" : ""
                    }`}
                    placeholder="Enter template name"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-500">
                      {validationErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description{" "}
                    <span className="text-gray-400">(Optional)</span>
                  </label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) =>
                      setNewTemplate({
                        ...newTemplate,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Enter template description"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Sections
                  </h4>
                  <div className="space-y-4">
                    {newTemplate.sections.map((section, index) => (
                      <div
                        key={index}
                        className="p-4 border rounded-lg bg-gray-50"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Section Title{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              {newTemplate.sections.length > 1 && (
                                <button
                                  onClick={() => removeSection(index)}
                                  className="ml-2 p-1 text-gray-400 hover:text-red-500"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) =>
                                updateSection(index, "title", e.target.value)
                              }
                              placeholder="Enter section title"
                              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                validationErrors.sections?.[index]?.title
                                  ? "border-red-500"
                                  : ""
                              }`}
                            />
                            {validationErrors.sections?.[index]?.title && (
                              <p className="mt-1 text-sm text-red-500">
                                {validationErrors.sections[index].title}
                              </p>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instructions <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={section.instructions}
                            onChange={(e) =>
                              updateSection(
                                index,
                                "instructions",
                                e.target.value
                              )
                            }
                            placeholder="Enter section instructions"
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              validationErrors.sections?.[index]?.instructions
                                ? "border-red-500"
                                : ""
                            }`}
                            rows={3}
                          />
                          {validationErrors.sections?.[index]?.instructions && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.sections[index].instructions}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={addSection}
                      className="w-full py-3 border-2 border-dashed border-blue-200 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Add Section
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTemplate(emptyTemplate);
                    setValidationErrors({});
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 min-w-[120px] justify-center"
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Template"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Delete Template
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedTemplate?.name}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTemplate}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIStudio;
